import {
  UserManager,
  UserManagerSettings,
  WebStorageStateStore,
} from "oidc-client-ts";

/**
 * Scopes requested from ZITADEL.
 *
 * This list deliberately does NOT contain `urn:zitadel:iam:org:project:roles`.
 * That scope does not exist — the real one is `urn:zitadel:iam:org:projects:roles`
 * (plural, and it means "roles of *every* project"). ZITADEL silently drops
 * unknown scopes during /oauth/v2/authorize, so it never reached the OIDC
 * session, but oidc-client-ts kept replaying the originally *requested* scope on
 * every token refresh (see `refreshTokenAllowedScope` below). ZITADEL rejects a
 * refresh whose scope is not a subset of the session scope, so every single
 * silent renew failed with `invalid_scope` and the session died the moment the
 * access token expired.
 *
 * Project roles are asserted into the JWT by the application's role-assertion
 * settings (`access_token_role_assertion` / `id_token_role_assertion`) and by the
 * project's own role assertion — never by this scope — so dropping it does not
 * change a single claim.
 */
const SCOPES = [
  "openid",
  "profile",
  "email",
  "offline_access",
  "urn:zitadel:iam:org:project:id:zitadel:aud",
].join(" ");

export const oidcSettings: UserManagerSettings = {
  authority: import.meta.env.VITE_ZITADEL_AUTHORITY,
  client_id: import.meta.env.VITE_ZITADEL_CLIENT_ID,
  redirect_uri: import.meta.env.VITE_ZITADEL_REDIRECT_URI, // Must match exactly what you put in ZITADEL
  response_type: "code",
  scope: SCOPES,

  // ZITADEL omits `scope` from its token response, so oidc-client-ts falls back
  // to persisting the scope we *asked* for and replays that verbatim on every
  // refresh. Pinning the allow-list here keeps the refresh request a guaranteed
  // subset of what ZITADEL actually stored on the session, even though ZITADEL
  // augments that session scope with one `urn:zitadel:iam:org:project:role:<key>`
  // entry per project role. Without it, any scope ZITADEL declines to store turns
  // into a permanent `invalid_scope` on refresh.
  refreshTokenAllowedScope: SCOPES,

  // Store tokens in localStorage so they persist across reloads
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  automaticSilentRenew: true,

  // NOTE: no `prompt: "login"`. Forcing it made every redirect — including
  // recovery from an expired token — a full credential re-entry, which is what
  // turned a failed renewal into a visible "you have been signed out". Without
  // it ZITADEL re-issues from the existing SSO session and the user barely
  // notices. Use `signinRedirect({ prompt: "login" })` at the call site if a
  // specific flow ever needs to force re-authentication.

  // After ZITADEL ends the session it redirects here. Must be registered in the
  // ZITADEL application settings as an allowed Post Logout Redirect URI.
  post_logout_redirect_uri:
    import.meta.env.VITE_ZITADEL_POST_LOGOUT_URI || `${window.location.origin}`,
};

/**
 * Shared UserManager instance.
 *
 * Passed to `<AuthProvider userManager={...}>` so the rest of the app can drive
 * the OIDC client directly. Calling `userManager.signinSilent()` instead of
 * `useAuth().signinSilent()` matters: the react-oidc-context wrapper flips
 * `isLoading` to true for the duration of the call, which would flash
 * ProtectedRoute's full-screen spinner over a perfectly usable app. The
 * UserManager still emits `userLoaded` on success, so AuthProvider state stays
 * in sync either way.
 */
export const userManager = new UserManager(oidcSettings);
